import React from 'react';
import { Box, Typography, TextField, Select, MenuItem, Autocomplete, Chip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { PlacesTextField } from '../../../../components/groups-teams/shared/PlacesTextField';

export interface TagOption {
    id: string;
    name: string;
    color: string;
    isNew?: boolean;
}

interface TeamDetailsCardProps {
    name: string;
    location: string;
    groupId: string | null;
    groupName: string | null;
    groups: { id: string; name: string }[];
    selectedTags: TagOption[];
    allTags: TagOption[];
    tagsLoading: boolean;
    onNameChange: (value: string) => void;
    onNameBlur?: () => void;
    onLocationChange: (value: string) => void;
    onLocationBlur?: () => void;
    onGroupChange: (groupId: string) => void;
    onTagsChange: (tags: TagOption[]) => void;
}

export const TeamDetailsCard: React.FC<TeamDetailsCardProps> = ({
    name,
    location,
    groupId,
    groups,
    selectedTags,
    allTags,
    tagsLoading,
    onNameChange,
    onNameBlur,
    onLocationChange,
    onLocationBlur,
    onGroupChange,
    onTagsChange,
}) => {
    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '14px' }}>
                <InfoIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Team Details</Typography>
            </Box>

            <FieldLabel>Team Name</FieldLabel>
            <TextField
                fullWidth
                size="small"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                onBlur={onNameBlur}
                sx={{ mb: '14px', '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
            />

            <FieldLabel>Location</FieldLabel>
            <Box sx={{ mb: '14px' }}>
                <PlacesTextField value={location} onChange={onLocationChange} onBlur={onLocationBlur} />
            </Box>

            <FieldLabel>Parent Group</FieldLabel>
            <Select
                fullWidth
                size="small"
                value={groupId || ''}
                onChange={(e) => onGroupChange(e.target.value)}
                sx={{ mb: '4px', fontSize: '14px', borderRadius: '8px' }}
            >
                {groups.map((g) => (
                    <MenuItem key={g.id} value={g.id} sx={{ fontSize: '14px' }}>
                        {g.name}
                    </MenuItem>
                ))}
            </Select>
            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint, mb: '14px' }}>
                Changing the group will update the team hierarchy and may affect leader assignments.
            </Typography>

            <FieldLabel>Tags</FieldLabel>
            <Autocomplete
                multiple
                freeSolo
                options={allTags.filter((t) => !selectedTags.some((s) => s.id === t.id))}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                value={selectedTags}
                loading={tagsLoading}
                onChange={(_, newVal) => {
                    const mapped = newVal.map((v) => {
                        if (typeof v === 'string') {
                            const existing = allTags.find(
                                (t) => t.name.toLowerCase() === v.toLowerCase()
                            );
                            return existing || { id: `new-${v}`, name: v, color: '#9E9E9E', isNew: true };
                        }
                        return v;
                    });
                    onTagsChange(mapped);
                }}
                filterOptions={(options, params) => {
                    const filtered = options.filter((o) =>
                        o.name.toLowerCase().includes(params.inputValue.toLowerCase())
                    );
                    if (
                        params.inputValue.trim() &&
                        !allTags.some((t) => t.name.toLowerCase() === params.inputValue.toLowerCase()) &&
                        !selectedTags.some((t) => t.name.toLowerCase() === params.inputValue.toLowerCase())
                    ) {
                        filtered.push({
                            id: `new-${params.inputValue}`,
                            name: params.inputValue.trim(),
                            color: '#9E9E9E',
                            isNew: true,
                        });
                    }
                    return filtered;
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.isNew ? (
                            <Typography sx={{ fontSize: '14px' }}>
                                Create "<strong>{option.name}</strong>"
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: option.color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
                            </Box>
                        )}
                    </li>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((tag, idx) => (
                        <Chip
                            {...getTagProps({ index: idx })}
                            key={tag.id}
                            label={tag.name}
                            size="small"
                            sx={{
                                backgroundColor: tag.color + '22',
                                color: tag.color,
                                fontWeight: 500,
                                fontSize: '12px',
                            }}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={selectedTags.length === 0 ? 'Type to search or create tags...' : ''}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                    />
                )}
            />
        </Box>
    );
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: GT_COLORS.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '.5px',
            mb: '4px',
        }}
    >
        {children}
    </Typography>
);
